<template>
  <header>
    <div class="page-header">
      <div
        class="menu-trigger"
        role="button"
        tabindex="0"
        @click="handleMobileMenuClick"
        @keydown.enter="handleMobileMenuClick">
        <ImagesSvg
          :icon="mobileMenuOpen ? 'close' : 'hamburger'"
          color="white"
          :width="30"
          :heigth="30" />
      </div>
      <div class="page-header__logo">
        <NuxtLink :to="'/'">
          <ImagesSvg icon="logo" :width="187" :heigth="24" />
        </NuxtLink>
      </div>
      <nav v-if="!mobileMenuOpen" class="navigation">
        <ul class="navigation__links">
          <li v-for="(item, index) in menuItems" :key="item.link ?? index">
            <NuxtLink
              :to="item.link ?? ''"
              :class="['menu-item', { active: item.selected }]"
              :target="item.target">
              <span>{{ item.title }}</span>
            </NuxtLink>
          </li>
        </ul>
      </nav>
    </div>
    <div class="mobile-nav__wrapper" :class="{ open: mobileMenuOpen }">
      <nav class="mobile-nav__container">
        <ul class="mobile-nav__links">
          <li v-for="(item, index) in menuItems" :key="item.link ?? index">
            <LinksIconTextLink
              :link="item.link ?? ''"
              :target="item.target ?? ''"
              icon="arrow-right"
              :text="item.title ?? ''"
              :gap="4"
              icon-color="white"
              text-color="white"
              :class="['menu-item', { active: item.selected }]"
              :has-hover-underline="false"
              :has-animate-icon="false"
              :has-font-bold="false" />
          </li>
        </ul>
      </nav>
    </div>
  </header>
</template>

<script lang="ts" setup>
import { BREAK_POINTS } from '@base/utils/constants';
import type { MenuItemResponseModel } from '@api';

interface MainHeader {
  menuItems: MenuItemResponseModel[];
}

defineProps<MainHeader>();

const mobileMenuOpen = ref<boolean>(false);
const innerWindowWidth = ref<number>();

const handleMobileMenuClick = (): void => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
};

const onWidthChange = (): number => (innerWindowWidth.value = window.innerWidth);
const windowWidth = computed(() => innerWindowWidth.value);

watch(windowWidth, () => {
  if (windowWidth.value !== undefined && windowWidth.value >= BREAK_POINTS.MD) {
    mobileMenuOpen.value = false;
  }
});

onMounted(() => {
  innerWindowWidth.value = window.innerWidth;
  window.addEventListener('resize', onWidthChange);
});

onUnmounted(() => {
  window.removeEventListener('resize', onWidthChange);
});
</script>

<style lang="scss" scoped>
.page-header {
  display: grid;
  grid-template-columns: px(20) px(30) 1fr px(20);
  padding-block: px(20) px(16);
  background: url('@shared-assets/icons/background.svg') repeat-x;
  background-position-x: calc(50% + px(4.5));
  background-size: px(405) px(6);
  grid-area: pageHeader;

  &__logo {
    display: flex;
    justify-content: flex-end;
    padding-left: px(20);
  }

  .navigation {
    display: none;
  }

  .menu-item {
    text-transform: uppercase;
  }

  .menu-trigger {
    grid-column: 2;
    width: 100%;
    height: px(30);
    background-color: var(--color-navy-blue);
    color: var(--color-white);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  @include media-breakpoint-up(lg) {
    grid-template-columns: 1fr auto 9fr 1fr;
    padding: px(60) px(30) px(36);
    color: var(--color-navy-blue);
    background-position-x: calc(50% + px(6));
    background-size: px(540) px(8);

    a {
      color: inherit;
    }

    &__logo {
      padding-inline: px(0) px(36);
    }

    .menu-trigger {
      all: initial;
      visibility: hidden;

      > * {
        display: none;
      }
    }

    .navigation {
      display: flex;
      min-height: px(48);

      &__links {
        display: flex;
        font-weight: normal;
      }

      li {
        padding-inline: px(16);
      }
    }

    .menu-item {
      position: relative;
      padding-bottom: px(24);
      cursor: pointer;
      @include reset-decoration-states;

      &.active::after {
        content: '';
        height: px(2);
        position: absolute;
        bottom: 0;
        left: 0;
        background-color: var(--color-navy-blue);
        width: 100%;
      }

      &::after {
        content: '';
        height: px(2);
        position: absolute;
        bottom: 0;
        left: 0;
        transition: width 0.2s ease;
        width: 0;
        background-color: var(--color-blue);
      }

      &:not(.active):hover,
      &:not(.active):active {
        color: var(--color-blue);

        &::after {
          width: 100%;
        }
      }
    }
  }
}

.mobile-nav {
  &__wrapper {
    display: block;
    height: calc(100vh - px(70));
    width: 100vw;
    position: fixed;
    top: px(70);
    left: 0;
    transform: translateX(-100%);
    transition: transform 0.2s;
    z-index: 999;

    &.open {
      transform: translateX(0);
    }
  }

  &__container {
    background-color: var(--color-blue);
    color: var(--color-white);
    height: 100%;
    width: 100%;
    padding: px(24) px(20);
  }

  &__links {
    li {
      display: block;
      padding-block: px(8);

      .menu-item {
        position: relative;
        text-transform: uppercase;
        font-weight: normal;
        padding-bottom: px(8);
        @include reset-decoration-states;

        &.active {
          > *:not(svg) {
            position: relative;
            width: 100%;
          }

          > *:not(svg)::after {
            content: '';
            height: px(2);
            position: absolute;
            bottom: px(-8);
            left: 0;
            background-color: var(--color-white);
            width: 100%;
          }
        }
      }
    }
  }
}
</style>
